import type { RedirectLoginOptions } from '@auth0/auth0-spa-js'
import type {
  AuthInitialScreen,
  AuthProvider,
  ProviderLogoutOptions,
  SessionInfo,
} from '@/features/iam/auth/AuthStore'
import {
  decodeTokenClaims,
  isMfaEnrolled,
} from '@/features/iam/auth/token-claims'

const MOCK_ISSUER = 'https://mock.taxfyle.auth0.com'

/**
 * Uses a token inserted by E2E tests for authentication, and the fallback
 * provider for everything else.
 */
export class MockTokenProvider implements AuthProvider {
  constructor(private readonly _fallback: AuthProvider) {}

  async fetchSessionInfo(): Promise<SessionInfo | null> {
    // Read on every call, so a token inserted after startup is picked up.
    // Real sessions never store a token under this key.
    const token = this._readMockToken()
    if (token) {
      const claims = decodeTokenClaims(token)
      return {
        token,
        authId: claims.sub,
        mfaEnabled: isMfaEnrolled(claims),
      }
    }

    return this._fallback.fetchSessionInfo()
  }

  handleRedirectCallback() {
    return this._fallback.handleRedirectCallback()
  }

  redirectToAuthorize(
    initialScreen: AuthInitialScreen,
    opts: RedirectLoginOptions | undefined,
  ) {
    return this._fallback.redirectToAuthorize(initialScreen, opts)
  }

  logout(opts: ProviderLogoutOptions) {
    // A mock session has no identity provider session to end; dropping the
    // token is the logout, otherwise the next load would sign straight back in.
    if (this._readMockToken()) {
      localStorage.removeItem('mockjwt')
      window.location.assign(opts.returnTo)
      return Promise.resolve()
    }

    return this._fallback.logout(opts)
  }

  /**
   * The E2E token, or `null` when none is stored or it wasn't issued by the
   * mock issuer.
   */
  private _readMockToken() {
    const token = localStorage.getItem('mockjwt')
    if (!token || decodeTokenClaims(token).iss !== MOCK_ISSUER) {
      return null
    }

    return token
  }
}
